/**
 * Instagram Posts Integration
 * Handles fetching and displaying Instagram posts with auto-refresh functionality
 */

class InstagramPosts {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.apiToken = container.dataset.apiToken;
    this.postsCount = parseInt(container.dataset.postsCount) || 6;
    this.autoRefresh = container.dataset.autoRefresh === 'true';
    this.refreshInterval = parseInt(container.dataset.refreshInterval) || 15;
    
    this.loadingState = container.querySelector('[data-loading-state]');
    this.postsContainer = container.querySelector('[data-posts-container]');
    this.errorState = container.querySelector('[data-error-state]');
    this.sliderControls = container.querySelector('[data-slider-controls]');
    
    this.cache = new Map();
    this.refreshTimer = null;
    this.isLoading = false;
    
    this.init();
  }

  async init() {
    if (!this.apiToken) {
      this.showError('Instagram API token not configured');
      return;
    }

    // Load initial content
    await this.loadPosts();
    
    // Setup auto-refresh if enabled
    if (this.autoRefresh) {
      this.setupAutoRefresh();
    }
    
    // Setup intersection observer for lazy loading
    this.setupIntersectionObserver();
    
    // Bind events
    this.bindEvents();
  }

  async loadPosts() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.showLoading();
    
    try {
      const posts = await this.fetchPosts();
      
      if (posts && posts.length > 0) {
        this.renderPosts(posts);
        this.showContent();
      } else {
        this.showError('No posts found');
      }
    } catch (error) {
      console.error('Error loading Instagram posts:', error);
      this.showError('Failed to load Instagram content');
    } finally {
      this.isLoading = false;
    }
  }

  async fetchPosts() {
    const cacheKey = `posts_${this.postsCount}`;
    const cached = this.cache.get(cacheKey);
    
    // Return cached data if less than 5 minutes old
    if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
      return cached.data;
    }

    try {
      // Instagram Basic Display API endpoint for media
      const response = await fetch(
        `https://graph.instagram.com/me/media?fields=id,media_type,media_url,thumbnail_url,caption,timestamp,permalink,children{media_url,media_type}&access_token=${this.apiToken}&limit=${this.postsCount}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      
      // Process posts
      const posts = data.data
        .slice(0, this.postsCount)
        .map(post => ({
          id: post.id,
          mediaUrl: post.media_url,
          thumbnailUrl: post.thumbnail_url || post.media_url,
          caption: post.caption || '',
          timestamp: post.timestamp,
          permalink: post.permalink,
          mediaType: post.media_type,
          isCarousel: post.media_type === 'CAROUSEL_ALBUM',
          childrenCount: post.children ? post.children.data.length : 0
        }));

      // Cache the results
      this.cache.set(cacheKey, {
        data: posts,
        timestamp: Date.now()
      });

      return posts;
    } catch (error) {
      console.error('Instagram API Error:', error);
      throw error;
    }
  }

  renderPosts(posts) {
    const postsHTML = posts.map((post, index) => {
      return `
        <li class="grid__item${this.shouldShowSlider() ? ' slider__slide' : ''}"
            data-post-id="${post.id}"
            style="--animation-order: ${index + 1};">
          <article class="instagram-post">
            <a href="${post.permalink}" 
               target="_blank" 
               rel="noopener noreferrer"
               aria-label="View post on Instagram">
              
              <div class="instagram-post__media">
                <img src="${post.thumbnailUrl}" 
                     alt="Instagram post"
                     loading="${index < 4 ? 'eager' : 'lazy'}"
                     width="400"
                     height="400">
              </div>
            </a>
          </article>
        </li>
      `;
    }).join('');

    this.postsContainer.innerHTML = postsHTML;
  }



  showLoading() {
    this.loadingState.style.display = 'block';
    this.postsContainer.style.display = 'none';
    this.errorState.style.display = 'none';
  }

  showContent() {
    this.loadingState.style.display = 'none';
    this.postsContainer.style.display = 'flex';
    this.errorState.style.display = 'none';
  }

  showError(message) {
    this.loadingState.style.display = 'none';
    this.postsContainer.style.display = 'none';
    this.errorState.style.display = 'block';
    
    // Update error message if provided
    const errorContent = this.errorState.querySelector('.instagram-posts__error-content p');
    if (errorContent && message) {
      errorContent.textContent = message;
    }
  }

  setupAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
    
    this.refreshTimer = setInterval(() => {
      this.loadPosts();
    }, this.refreshInterval * 60 * 1000);
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;
    
    this.imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            this.imageObserver.unobserve(img);
          }
        }
      });
    }, {
      rootMargin: '50px'
    });

    // Use MutationObserver instead of deprecated DOMNodeInserted
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.tagName === 'IMG' && node.dataset.src) {
              this.imageObserver.observe(node);
            }
            // Check child images
            const images = node.querySelectorAll?.('img[data-src]');
            images?.forEach(img => this.imageObserver.observe(img));
          }
        });
      });
    });

    this.mutationObserver.observe(this.container, {
      childList: true,
      subtree: true
    });
  }

  bindEvents() {
    // Store bound functions for cleanup
    this.boundVisibilityChange = () => {
      if (document.hidden) {
        if (this.refreshTimer) {
          clearInterval(this.refreshTimer);
        }
      } else if (this.autoRefresh) {
        this.setupAutoRefresh();
      }
    };

    this.boundFocus = () => {
      if (this.autoRefresh && !this.refreshTimer) {
        this.setupAutoRefresh();
      }
    };

    this.boundBlur = () => {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    };

    // Handle visibility change to pause/resume auto-refresh
    document.addEventListener('visibilitychange', this.boundVisibilityChange);

    // Handle window focus/blur
    window.addEventListener('focus', this.boundFocus);
    window.addEventListener('blur', this.boundBlur);
  }

  shouldShowSlider() {
    const isMobile = window.innerWidth <= 749;
    const isTablet = window.innerWidth <= 989;
    
    if (isMobile || isTablet) {
      return this.postsCount > 2;
    }
    
    return this.container.dataset.enableDesktopSlider === 'true' && 
           this.postsCount > parseInt(this.container.dataset.columnsDesktop || 3);
  }

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  destroy() {
    // Clear timers
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }

    // Clear cache
    this.cache.clear();

    // Disconnect observers
    if (this.imageObserver) {
      this.imageObserver.disconnect();
      this.imageObserver = null;
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    // Remove event listeners
    if (this.boundVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    }
    if (this.boundFocus) {
      window.removeEventListener('focus', this.boundFocus);
    }
    if (this.boundBlur) {
      window.removeEventListener('blur', this.boundBlur);
    }
  }
}

// Initialize Instagram Posts sections
document.addEventListener('DOMContentLoaded', () => {
  const postsSections = document.querySelectorAll('[id^="instagram-posts-"]');
  
  postsSections.forEach(section => {
    new InstagramPosts(section);
  });
});

// Handle theme editor updates
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[id^="instagram-posts-"]');
    if (section) {
      new InstagramPosts(section);
    }
  });
}